import React from 'react';
import { useSearchParams } from 'react-router-dom';
import type { NodeInspectorProps } from './types';
import {
    ShieldCheck,
    User,
    Database,
    FileText,
    ArrowDownLeft,
    ArrowUpRight,
    Tag,
    Link as LinkIcon,
    AlertCircle,
} from 'lucide-react';

import { useForensicOverlayStore } from '../../stores/useForensicOverlayStore';
import { Skeleton, SkeletonList } from '../ui/Skeleton';


export const ForensicTab: React.FC<NodeInspectorProps> = ({ selectedNode, dossier }) => {
    const [searchParams] = useSearchParams();
    const caseId = searchParams.get('case_id');

    // Prefer the overlay store (Phase 2 canvas bridge). Legacy fallback keeps
    // any data served by the addresses/merge layer still rendering during
    // rollout.
    const overlay = caseId ? useForensicOverlayStore(s => s.byCase[caseId]) : null;
    const isLoading = caseId ? !!useForensicOverlayStore(s => s.loading[caseId]) : false;
    const fetchError = caseId ? useForensicOverlayStore(s => s.error[caseId]) : null;
    const legacyRecords = (dossier?.sections?.profile as any)?.forensic_records || [];

    // Self-heal: if a case_id is in the URL but nothing has fetched the overlay
    // yet (e.g. user opened the inspector before the Workbench load effect
    // ran, or the case hasn't been mounted in this Workbench instance), trigger
    // it from here. Idempotent — the store sets a per-case loading flag and
    // skips if already in flight.
    React.useEffect(() => {
        if (!caseId) return;
        if (overlay !== undefined && overlay !== null) return; // already populated (or explicit null after error)
        if (isLoading) return;
        useForensicOverlayStore.getState().fetch(caseId);
    }, [caseId, overlay, isLoading]);

    // GraphNodeData is FLAT — id and chain are top-level, not under .data
    const address = selectedNode?.id || '';
    const chain = (selectedNode?.chain || '').toUpperCase();

    // Address comparison: EVM addresses normalize to lowercase upstream; TRX/BTC preserve case.
    // Be case-insensitive on the address too so a click on a node spelled with different
    // case still matches the overlay record. Also fall back to address-only
    // match (no chain) when the canvas node's chain is missing or set to a
    // generic value — happens for nodes added by some algorithm paths that
    // don't set .chain reliably.
    const overlayNode = overlay && address
        ? overlay.nodes.find(n => {
            const sameAddr = n.address === address || n.address.toLowerCase() === address.toLowerCase();
            if (!sameAddr) return false;
            if (!chain) return true;
            return (n.chain || '').toUpperCase() === chain;
          })
        : undefined;
    const overlayRecords = overlayNode && overlay
        ? overlay.records.filter(r => overlayNode.record_ids.includes(r.id))
        : [];
    const lowerAddr = address.toLowerCase();
    const overlayEdges = overlay && address
        ? overlay.edges.filter(e => {
            const tgt = (e.target_address || '').toLowerCase();
            const src = (e.source_address || '').toLowerCase();
            return tgt === lowerAddr || src === lowerAddr;
          })
        : [];

    const hasAny = !!overlayNode || legacyRecords.length > 0;
    if (!hasAny) {
        // Differentiate the empty-state messages so the user can tell whether
        // the overlay is still loading, failed to load, returned nothing for
        // the case, or simply doesn't have a row for this address.
        let title = 'No Forensic Records';
        let body = 'No deterministic exchange responses or LEA uploads match this address in the current case.';
        if (!caseId) {
            title = 'No Case Selected';
            body = 'Forensic records attach to a case. Open this workbench from a case to see uploaded subpoenas.';
        } else if (isLoading || overlay === undefined) {
            return (
                <div className="p-4 space-y-4" role="status" aria-label="Loading forensic overlay">
                    <Skeleton className="h-4 w-40" />
                    <div className="bg-secondary/20 border border-border/40 rounded-md p-3 space-y-2">
                        <Skeleton className="h-3 w-16" />
                        <Skeleton className="h-4 w-3/4" />
                        <Skeleton className="h-3 w-1/2" />
                    </div>
                    <SkeletonList count={3} label="Loading forensic overlay" />
                </div>
            );
        } else if (fetchError) {
            title = 'Overlay Fetch Failed';
            body = `The forensic overlay endpoint returned an error: ${fetchError}. Click any node again after the network recovers, or refresh the page.`;
        } else if (overlay && overlay.nodes.length === 0) {
            title = 'No Forensic Records';
            body = 'This case has no uploaded subpoenas or LEA records yet. Use the Forensic Locker to upload an exchange response or freeform CSV.';
        } else if (overlay && overlay.nodes.length > 0) {
            title = 'Address Not in Subpoena';
            body = `This case has ${overlay.nodes.length} addresses in the forensic overlay, but ${address.slice(0, 10)}… is not one of them. The address may be a discovered counterparty rather than a subpoenaed wallet.`;
        }
        return (
            <div className="p-8 text-center flex flex-col items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-secondary/50 flex items-center justify-center">
                    <Database className="w-6 h-6 text-muted-foreground/40" />
                </div>
                <div className="space-y-1">
                    <p className="text-sm font-medium">{title}</p>
                    <p className="text-xs text-muted-foreground max-w-[280px] mx-auto">{body}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="p-4 space-y-5">
            <div className="flex items-center gap-2 border-b border-border pb-2">
                <ShieldCheck className="w-4 h-4 text-blue-500" />
                <h3 className="text-sm font-bold uppercase tracking-tight text-blue-400">
                    Forensic Evidence
                </h3>
                {overlayNode && (
                    <span
                        className={`ml-auto text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${
                            overlayNode.kind === 'forensic_ghost'
                                ? 'border-gray-500/40 text-gray-300 bg-gray-500/10'
                                : 'border-blue-500/40 text-blue-400'
                        }`}
                    >
                        {overlayNode.kind === 'forensic_self'
                            ? 'Subject'
                            : overlayNode.kind === 'forensic_ghost'
                                ? 'Undisclosed'
                                : 'Counterparty'}
                    </span>
                )}
            </div>

            {overlayNode?.kind === 'forensic_ghost' && (
                <div className="bg-gray-500/10 border border-gray-500/30 rounded-md p-3 text-[11px] space-y-1.5">
                    <p className="font-bold text-gray-200 flex items-center gap-1.5">
                        <AlertCircle className="w-3 h-3 text-gray-400" />
                        Counterparty withheld in subpoena
                    </p>
                    <p className="text-muted-foreground leading-relaxed">
                        The exchange disclosed this movement (date, amount{overlayEdges.some(e => e.tx_hash) ? ', and on-chain hash' : ''})
                        but did not provide the wallet on the other side. This is common for withdrawals — exchanges typically
                        don't expose their hot-wallet addresses.
                    </p>
                    {overlayEdges.some(e => e.tx_hash) && (
                        <p className="text-emerald-400/80 leading-relaxed">
                            Once the on-chain transaction is fetched, this placeholder can be promoted to the real address
                            (Tier 1a auto-link).
                        </p>
                    )}
                </div>
            )}

            {/* Aggregated KYC across all records touching this address */}
            {overlayNode && (overlayNode.kyc.names.length + overlayNode.kyc.emails.length + overlayNode.kyc.exchanges.length) > 0 && (
                <div className="space-y-2">
                    <p className="text-[10px] text-muted-foreground uppercase font-bold flex items-center gap-1">
                        <User className="w-3 h-3" /> Identity (from subpoena)
                    </p>
                    <div className="bg-secondary/20 border border-border/40 rounded-md p-3 space-y-1.5 text-xs">
                        {overlayNode.kyc.names.length > 0 && (
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">Name</span>
                                <span className="font-bold text-foreground text-right">{overlayNode.kyc.names.join(', ')}</span>
                            </div>
                        )}
                        {overlayNode.kyc.emails.length > 0 && (
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">Email</span>
                                <span className="font-medium text-blue-400 text-right break-all">{overlayNode.kyc.emails.join(', ')}</span>
                            </div>
                        )}
                        {overlayNode.kyc.exchange_user_ids.length > 0 && (
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">Exchange UID</span>
                                <span className="font-mono text-foreground bg-background px-1 rounded border border-border/50">
                                    {overlayNode.kyc.exchange_user_ids.join(', ')}
                                </span>
                            </div>
                        )}
                        {overlayNode.kyc.exchanges.length > 0 && (
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">Exchange</span>
                                <span className="font-medium text-foreground">{overlayNode.kyc.exchanges.join(', ')}</span>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Freeform labels (from FreeformParser `label` column) */}
            {overlayNode && overlayNode.labels.length > 0 && (
                <div className="space-y-2">
                    <p className="text-[10px] text-muted-foreground uppercase font-bold flex items-center gap-1">
                        <Tag className="w-3 h-3" /> Labels
                    </p>
                    <div className="flex flex-wrap gap-1">
                        {overlayNode.labels.map((l, i) => (
                            <span key={i} className="px-2 py-0.5 rounded-full bg-blue-500/10 border border-blue-500/30 text-blue-300 text-[10px] font-medium">
                                {l}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {/* Source files */}
            {overlayRecords.length > 0 && (
                <div className="space-y-2">
                    <p className="text-[10px] text-muted-foreground uppercase font-bold flex items-center gap-1">
                        <FileText className="w-3 h-3" /> Source Records ({overlayRecords.length})
                    </p>
                    <div className="space-y-1">
                        {overlayRecords.map(r => (
                            <div key={r.id} className="bg-secondary/20 border border-border/40 rounded p-2 flex items-center justify-between text-[11px]">
                                <div className="min-w-0">
                                    <div className="font-medium truncate">{r.file_name}</div>
                                    <div className="text-muted-foreground text-[10px]">
                                        {r.parser || r.source_exchange || r.record_type}
                                        {r.movements_total ? ` · ${r.movements_total} movements` : ''}
                                    </div>
                                </div>
                                {r.movements_with_hash !== undefined && (
                                    <div className="ml-2 flex items-center gap-1">
                                        <span className="text-[9px] text-emerald-500 font-bold">✓{r.movements_with_hash}</span>
                                        {r.movements_internal_id_only ? (
                                            <span className="text-[9px] text-amber-500 font-bold">~{r.movements_internal_id_only}</span>
                                        ) : null}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Related movements */}
            {overlayEdges.length > 0 && (
                <div className="space-y-2">
                    <p className="text-[10px] text-muted-foreground uppercase font-bold flex items-center gap-1">
                        <LinkIcon className="w-3 h-3" /> Subpoena Movements ({overlayEdges.length})
                    </p>
                    <div className="space-y-1 max-h-60 overflow-y-auto">
                        {overlayEdges.slice(0, 50).map((e, i) => {
                            const isDeposit = e.direction === 'deposit';
                            const Icon = isDeposit ? ArrowDownLeft : ArrowUpRight;
                            const color = isDeposit ? 'text-emerald-400' : 'text-amber-400';
                            return (
                                <div key={i} className="bg-secondary/10 border border-border/40 rounded p-2 text-[10px]">
                                    <div className="flex items-center gap-1.5">
                                        <Icon className={`w-3 h-3 ${color}`} />
                                        <span className="font-bold uppercase text-[9px]">{e.direction}</span>
                                        {e.amount && e.currency && (
                                            <span className="font-mono text-foreground">{e.amount} {e.currency}</span>
                                        )}
                                        {!e.verified && (
                                            <span className="ml-auto text-[8px] text-amber-500 font-bold uppercase">int-id only</span>
                                        )}
                                        {e.verified && (
                                            <span className="ml-auto text-[8px] text-emerald-500 font-bold uppercase">on-chain</span>
                                        )}
                                    </div>
                                    {e.tx_hash && (
                                        <div className="font-mono text-[9px] text-muted-foreground truncate mt-0.5">
                                            {e.tx_hash}
                                        </div>
                                    )}
                                    {e.timestamp && (
                                        <div className="text-[9px] text-muted-foreground/70 mt-0.5">{e.timestamp}</div>
                                    )}
                                </div>
                            );
                        })}
                        {overlayEdges.length > 50 && (
                            <div className="text-[10px] text-muted-foreground text-center pt-1">
                                +{overlayEdges.length - 50} more
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Legacy fallback — only shown if overlay is empty but the old merge layer has data */}
            {!overlayNode && legacyRecords.length > 0 && (
                <div className="space-y-3 border-t border-border/40 pt-3">
                    <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                        <AlertCircle className="w-3 h-3" />
                        <span>Legacy record (pre-Phase 2 format)</span>
                    </div>
                    {legacyRecords.map((record: any, idx: number) => (
                        <div key={idx} className="bg-secondary/20 border border-border/40 rounded p-2 text-[11px]">
                            <div className="font-medium">{record.file}</div>
                            <div className="text-muted-foreground">{record.exchange}</div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
